import { Close } from "@mui/icons-material";
import styled from "@emotion/styled";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import moment from "moment";
import EtDatePicker from "mui-ethiopian-datepicker";
import { forwardRef } from "react";

const StyledBackgroundInputField = styled(EtDatePicker)(
  ({ theme, neutral, rounded }) => ({
    "& .MuiInputBase-root": {
      borderRadius: rounded ? "4rem" : "0.5rem",
      backgroundColor: `${
        neutral ? theme.palette.neutral.main : theme.palette.secondary.main
      }`,
      "&.Mui-disabled": {
        backgroundColor: neutral
          ? theme.palette.neutral.light || "#f5f5f5"
          : theme.palette.secondary.light || "#e0e0e0",
        color: theme.palette.text.disabled || "#9e9e9e",
        "& fieldset": {
          borderColor: theme.palette.grey[300] || "#d1d5db"
        },
        "& .MuiInputBase-input": {
          WebkitTextFillColor: theme.palette.text.disabled || "#9e9e9e"
        }
      }
    },
    "& .MuiInputBase-input": {
      padding: "8px 16px"
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: `none`
    },
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        border: `none`
      }
    }
  })
);

const StyledBorderedInputField = styled(EtDatePicker)(({ theme, neutral }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "0.5rem",
    "& fieldset": {
      borderWidth: "2px",
      borderStyle: "solid",
      borderColor: `${theme.palette.primary.main}`
    },
    "&:hover fieldset": {
      borderColor: `${theme.palette.primary.main}`
    },
    "&.Mui-focused fieldset": {
      borderColor: `${theme.palette.primary.main}`
    },
    "&.Mui-disabled": {
      backgroundColor: neutral
        ? theme.palette.neutral.light || "#f5f5f5"
        : theme.palette.grey[100] || "#f5f5f5",
      "& fieldset": {
        borderColor: theme.palette.grey[300] || "#d1d5db"
      },
      "& .MuiInputBase-input": {
        WebkitTextFillColor: theme.palette.text.disabled || "#9e9e9e"
      }
    }
  },
  "& .MuiInputBase-input": {
    padding: "8px 16px"
  }
}));

export const DateField = forwardRef(
  (
    {
      label,
      isLabelBold,
      hasNoBg,
      isPlain,
      width = "100%",
      required,
      // disablePastDates,
      // disableFutureDates,
      name,
      value,
      onChange,
      ...rest
    },
    ref
  ) => {
    // Convert value to Date object or null for EtDatePicker
    const dateValue = value ? moment(value).toDate() : null;

    // Handle date changes from the picker
    const handleChange = newDate => {
      if (newDate) {
        onChange({ target: { value: moment(newDate).format("YYYY-MM-DD") } });
      } else {
        // Emit null (not "") on clear. yup's .date().nullable() accepts null
        // but tries to cast "" to a Date, which fails with "Invalid Date".
        onChange({ target: { value: null } });
      }
    };

    // Clear the selected date. stopPropagation prevents the click from
    // bubbling to the input, which would otherwise reopen the date picker.
    const handleClear = event => {
      event.stopPropagation();
      handleChange(null);
    };

    const showClear = !!value && !rest.disabled;

    const InputField = hasNoBg
      ? StyledBorderedInputField
      : StyledBackgroundInputField;

    return (
      <Stack width={width}>
        {!!label && (
          <Typography
            variant="subtitle2"
            component="label"
            htmlFor={name}
            sx={{
              fontWeight: isLabelBold && 700,
              "&:after": {
                content: required ? '" *"' : '""',
                color: "red"
              }
            }}
          >
            {label}
          </Typography>
        )}
        <Box
          sx={{
            position: "relative",
            display: "inline-flex",
            alignSelf: "flex-start",
            maxWidth: "100%"
          }}
        >
          <InputField
            id={name}
            name={name}
            size="small"
            variant={hasNoBg && isPlain ? "standard" : undefined}
            // disablePast={disablePastDates}
            // disableFuture={disableFutureDates}
            inputRef={ref}
            value={dateValue}
            onChange={handleChange}
            key={value || "empty"}
            {...rest}
          />
          {showClear && (
            <IconButton
              aria-label="clear date"
              size="small"
              onClick={handleClear}
              sx={{
                position: "absolute",
                top: "50%",
                right: 48,
                transform: "translateY(-50%)"
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Stack>
    );
  }
);

DateField.displayName = "DateField";
